import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { compact, first } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormAwareTypeaheadSelectInput,
  FormAwareTypeaheadSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { BUSINESS_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { GRATDesignerTrowser } from '@/modules/entities/gratTrusts/GRATDesignerTrowser/GRATDesignerTrowser';
import { inKindTransferToggleCopy } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import { GratDesignerStages } from '@/navigation/constants';
import { formatCurrency } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';
import { validateNumberRange } from '@/utils/validators/validateNumberRange';

import { calculateTotalValue } from '../../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.utils';
import { SelectedAssetDetails } from '../../EstateWaterfallHypotheticalTransfers/SelectedAssetDetails';
import {
  computeTransferAmount,
  getTransferDiscountHelpText,
} from '../../EstateWaterfallHypotheticalTransfers/transferDiscount.utils';
import { TransferFundingSource } from '../../EstateWaterfallHypotheticalTransfers/TransferFundingSource';
import {
  HypotheticalGratFormPaths,
  HypotheticalGratFormShape,
  SourceValues,
} from '../EstateWaterfallHypotheticalGratModal.types';
import { GratDesignerFooter } from './GratDesignerFooter';

export function TransferNameField() {
  const { control } = useFormContext<HypotheticalGratFormShape>();

  return (
    <FormAwareTextInput<HypotheticalGratFormShape>
      control={control}
      fieldName={'displayName' as const satisfies HypotheticalGratFormPaths}
      label="Transfer name"
      required
      placeholder="Specify transfer name"
    />
  );
}

interface SimulatedGratSelectFieldProps {
  householdId: string;
  gratOptions: FormAwareTypeaheadSelectInputProps<HypotheticalGratFormShape>['options'];
}

export function SimulatedGratSelectField({
  householdId,
  gratOptions,
}: SimulatedGratSelectFieldProps) {
  const { control, setValue } = useFormContext<HypotheticalGratFormShape>();

  const [savedGratId, setSavedGratId] = useState<string | null>(null);

  const selectedGratId = useWatch({
    control,
    name: 'simulatedGratId',
  });

  const [
    { isModalOpen: isDraftGratModalOpen },
    { openModal: openDraftGratModal, closeModal: closeDraftGratModal },
  ] = useModalState();

  const handleFormAction = (
    action?: 'cancel' | 'basicInformation' | 'structuring',
    gratId?: string
  ) => {
    if (action === 'basicInformation') {
      return;
    }

    if (gratId) {
      setSavedGratId(gratId);
    }

    closeDraftGratModal();
  };

  const handleAddNewOption = () => {
    setValue('simulatedGratId', '');
    openDraftGratModal();
  };

  useEffect(() => {
    if (
      savedGratId &&
      savedGratId !== selectedGratId &&
      gratOptions.find((o) => o.value === savedGratId && !o.disabled)
    ) {
      setValue('simulatedGratId', savedGratId);
      setSavedGratId(null);
    }
  }, [gratOptions, savedGratId, selectedGratId, setValue]);

  return (
    <>
      {isDraftGratModalOpen && (
        <GRATDesignerTrowser
          isOpen={true}
          householdId={householdId}
          initialStage={GratDesignerStages.BASIC_INFORMATION}
          onFormAction={(action, gratId) => handleFormAction(action, gratId)}
          Footer={GratDesignerFooter}
          entityId={selectedGratId || undefined}
        />
      )}
      <Stack direction="row" spacing={2}>
        <Box sx={{ flexGrow: 1 }}>
          <FormAwareTypeaheadSelectInput<HypotheticalGratFormShape>
            control={control}
            fieldName={
              'simulatedGratId' as const satisfies HypotheticalGratFormPaths
            }
            label="GRAT details"
            groupBy={(o) => o.groupName ?? ''}
            required
            options={gratOptions}
            addNewOption={{
              text: 'Create new hypothetical GRAT',
              icon: <PlusIcon size={16} />,
              onClick: handleAddNewOption,
            }}
            placeholder="Select existing hypothetical GRAT or create new"
          />
        </Box>
        {selectedGratId && (
          <Stack justifyContent="end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => openDraftGratModal()}
            >
              Edit details
            </Button>
          </Stack>
        )}
      </Stack>
    </>
  );
}

interface SourceEntitySelectFieldProps {
  sourceEntityOptions: FormAwareTypeaheadSelectInputProps<HypotheticalGratFormShape>['options'];
  sourceIndividualOptions: FormAwareTypeaheadSelectInputProps<HypotheticalGratFormShape>['options'];
}

export function SourceEntitySelectField({
  sourceEntityOptions,
  sourceIndividualOptions,
}: SourceEntitySelectFieldProps) {
  const { control } = useFormContext<HypotheticalGratFormShape>();
  const options = useMemo(() => {
    return [...sourceEntityOptions, ...sourceIndividualOptions];
  }, [sourceEntityOptions, sourceIndividualOptions]);

  return (
    <FormAwareTypeaheadSelectInput<HypotheticalGratFormShape>
      control={control}
      fieldName={'sourceId' as const satisfies HypotheticalGratFormPaths}
      label="From"
      groupBy={(o) => o.groupName ?? ''}
      required
      options={options}
      placeholder="Select funding source"
    />
  );
}

interface FundingRadioGroupFieldProps {
  sourceValues: Record<string, SourceValues>;
}

export function FundingRadioGroupField({
  sourceValues,
}: FundingRadioGroupFieldProps) {
  const { control } = useFormContext<HypotheticalGratFormShape>();

  const [proRataPattern, sourceId] = useWatch({
    control,
    name: ['proRataPattern', 'sourceId'],
  });

  const assetOptions = useMemo(() => {
    const sourceValue = sourceValues[sourceId];

    if (!sourceValue) {
      return [];
    }

    return Object.entries(sourceValue.byClassId).map(([id, details]) => ({
      value: id,
      display: `${details.displayName} (${formatCurrency(details.totalValueForClass, { notation: 'compact' })})`,
      groupName:
        details.hasOwnershipStake === null
          ? 'Asset classes'
          : 'Business entities',
    }));
  }, [sourceId, sourceValues]);

  const totalSourceValue = sourceValues[sourceId]?.totalValue ?? new Decimal(0);

  return (
    <TransferFundingSource<HypotheticalGratFormShape>
      control={control}
      displayKind="amount"
      proRataPattern={proRataPattern}
      sourceId={sourceId}
      sourceValue={totalSourceValue}
      assetOptions={assetOptions}
      helpTextAssetClassesAndBusinesses="Select a source entity to see available assets"
    />
  );
}

interface SelectedAssetDetailsFieldProps {
  sourceValues: Record<string, SourceValues>;
}

export function SelectedAssetDetailsField({
  sourceValues,
}: SelectedAssetDetailsFieldProps) {
  const { control } = useFormContext<HypotheticalGratFormShape>();

  const [
    sourceId,
    assetsPattern,
    fundingKind,
    selectedAssetClassesAndBusinessIds,
    selectedAssetClassAndBusinessValues,
  ] = useWatch({
    control,
    name: [
      'sourceId',
      'assetsPattern',
      'fundingKind',
      'selectedAssetClassesAndBusinessIds',
      'selectedAssetClassAndBusinessValues',
    ],
  });

  const assetOptionDetails = sourceValues[sourceId];

  const selectedAssetDetails = useMemo(() => {
    if (!selectedAssetClassesAndBusinessIds || !assetOptionDetails) return [];
    return compact(
      selectedAssetClassesAndBusinessIds.map((id) => {
        const details = assetOptionDetails.byClassId[id];
        if (!details) return null;
        return {
          id,
          ...details,
        };
      })
    ).sort((a, b) => {
      return a.totalValueForClass.comparedTo(b.totalValueForClass);
    });
  }, [selectedAssetClassesAndBusinessIds, assetOptionDetails]);

  const totalSelectedValue = useMemo(() => {
    const classIdToValue: Record<
      string,
      {
        totalValue: Decimal;
      }
    > = {};

    Object.entries(assetOptionDetails?.byClassId ?? {}).forEach(
      ([classId, value]) => {
        classIdToValue[classId] = {
          totalValue: value.totalValueForClass,
        };
      }
    );

    return calculateTotalValue({
      selectedAssetIds: selectedAssetClassesAndBusinessIds,
      selectedAssetClassAndBusinessValues,
      assetOptionDetails: classIdToValue,
      pattern: assetsPattern,
    });
  }, [
    assetOptionDetails?.byClassId,
    assetsPattern,
    selectedAssetClassAndBusinessValues,
    selectedAssetClassesAndBusinessIds,
  ]);

  return (
    <SelectedAssetDetails
      control={control}
      fundingKind={fundingKind}
      selectedAssetDetails={selectedAssetDetails}
      assetsPattern={assetsPattern}
      totalSelectedValue={totalSelectedValue}
    />
  );
}

interface TaxDiscountFieldProps {
  sourceValues: Record<string, SourceValues>;
}

export function TaxDiscountCard({ sourceValues }: TaxDiscountFieldProps) {
  const { control } = useFormContext<HypotheticalGratFormShape>();
  const [
    entityTaxDiscountRate,
    applyEntityTaxDiscount,
    sourceId,
    selectedAssetClassesAndBusinessIds,
    fundingKind,
    proRataPattern,
    proRataAmount,
    proRataPercent,
    assetsPattern,
    selectedAssetClassAndBusinessValues,
  ] = useWatch({
    control,
    name: [
      'entityTaxDiscountRate',
      'applyEntityTaxDiscount',
      'sourceId',
      'selectedAssetClassesAndBusinessIds',
      'fundingKind',
      'proRataPattern',
      'proRataAmount',
      'proRataPercent',
      'assetsPattern',
      'selectedAssetClassAndBusinessValues',
    ],
  });

  const applyEntityDiscountControlLabel: string = useMemo(() => {
    if (sourceValues[sourceId]?.effectiveDiscountPercent?.gt(0)) {
      return 'Override existing discount';
    }

    return 'Apply discount';
  }, [sourceId, sourceValues]);

  const applyEntityTaxDiscountHelpText: string | undefined = useMemo(() => {
    const sourceNode = sourceValues[sourceId];
    if (!sourceNode) return undefined;

    let discountRate: Decimal | null | undefined =
      sourceNode?.effectiveDiscountPercent;

    const firstAssetClassId = first(selectedAssetClassesAndBusinessIds);
    const { entityType: firstAssetClassEntityType, effectiveDiscountPercent } =
      (firstAssetClassId && sourceNode.byClassId[firstAssetClassId]) || {};
    const isFirstAssetClassBusinessEntity =
      firstAssetClassEntityType &&
      BUSINESS_ENTITY_TYPES.includes(firstAssetClassEntityType);

    if (
      selectedAssetClassesAndBusinessIds?.length === 1 &&
      isFirstAssetClassBusinessEntity
    ) {
      discountRate = effectiveDiscountPercent;
    }

    if (discountRate?.gt(0)) {
      return `Source entity has an effective ${formatPercent(discountRate, 2)}% valuation discount`;
    }
    return undefined;
  }, [selectedAssetClassesAndBusinessIds, sourceId, sourceValues]);

  const totalAssetsValue = useMemo(() => {
    const sourceNode = sourceValues[sourceId];
    if (!sourceNode) return null;

    const classIdToValue: Record<string, { totalValue: Decimal }> = {};
    Object.entries(sourceNode.byClassId).forEach(([classId, value]) => {
      classIdToValue[classId] = {
        totalValue: value.totalValueForClass,
      };
    });

    return calculateTotalValue({
      selectedAssetIds: selectedAssetClassesAndBusinessIds,
      selectedAssetClassAndBusinessValues,
      assetOptionDetails: classIdToValue,
      pattern: assetsPattern,
    });
  }, [
    assetsPattern,
    selectedAssetClassAndBusinessValues,
    selectedAssetClassesAndBusinessIds,
    sourceId,
    sourceValues,
  ]);

  const transferAmount = useMemo(
    () =>
      computeTransferAmount({
        fundingKind,
        proRataPattern,
        proRataAmount,
        proRataPercent,
        totalSourceValue: sourceValues[sourceId]?.totalValue,
        totalAssetsValue,
      }),
    [
      fundingKind,
      proRataPattern,
      proRataAmount,
      proRataPercent,
      sourceId,
      sourceValues,
      totalAssetsValue,
    ]
  );

  const entityTaxDiscountRateHelpText = useMemo(() => {
    if (!applyEntityTaxDiscount) return undefined;
    return getTransferDiscountHelpText({
      transferAmount,
      discountRate: entityTaxDiscountRate,
    });
  }, [applyEntityTaxDiscount, entityTaxDiscountRate, transferAmount]);

  return (
    <Card variant="filled" sx={{ p: 2, pb: 4 }}>
      <Stack spacing={2}>
        <FormAwareSwitch<HypotheticalGratFormShape>
          control={control}
          fieldName={
            'applyEntityTaxDiscount' as const satisfies HypotheticalGratFormPaths
          }
          label={applyEntityDiscountControlLabel}
          labelPosition="right"
          helpText={applyEntityTaxDiscountHelpText}
        />
        {applyEntityTaxDiscount && (
          <Box sx={{ pl: 7 }}>
            <FormAwarePercentInput<HypotheticalGratFormShape>
              control={control}
              fieldName={
                'entityTaxDiscountRate' as const satisfies HypotheticalGratFormPaths
              }
              label="Entity tax discount rate"
              helpText={entityTaxDiscountRateHelpText}
              isDecimalJSInput
              hideLabel
              required
              decimalScale={2}
              validation={{
                minMax: validateNumberRange({
                  min: 0,
                  max: 100,
                  fieldName: 'Discount rate',
                }),
              }}
            />
          </Box>
        )}
        <FormAwareSwitch<HypotheticalGratFormShape>
          labelPosition="right"
          fieldName={
            'forceLiquidationOnTransfer' as const satisfies HypotheticalGratFormPaths
          }
          {...inKindTransferToggleCopy}
          control={control}
        />
      </Stack>
    </Card>
  );
}
